import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { sec } from "../../../security";
import { addNotification } from "../../notifications/notificationsSlice";
import { JobNote, JobNotesResponse, JobNotesView } from "./model/jobNote";

const apiServerUrl = process.env.REACT_APP_HAISTACK_API_URL;

export const jobNotesApi = createApi({
  reducerPath: "jobNotesApi",
  tagTypes: ["JobNotes", "JobNote"],
  baseQuery: fetchBaseQuery({
    baseUrl: apiServerUrl,
    prepareHeaders: async (headers) => {
      const token = await sec.getAccessTokenSilently()();
      if (token) headers.set("authorization", `Bearer ${token}`);
      return headers;
    }
  }),
  endpoints: (builder) => ({
    getJobNotes: builder.query<JobNotesView, string>({
      query: (qs) => `/api/v2/job_notes?${qs}`,
      transformResponse: (response: JobNotesResponse): JobNotesView => ({
        data: response.job_notes,
        count: response.meta?.count ?? 0
      }),
      providesTags: ["JobNotes"]
    }),
    getJobNote: builder.query<JobNote, string>({
      query: (id) => `/api/v2/job_notes/${id}`,
      transformResponse: (response: { job_note: JobNote }) => response.job_note,
      providesTags: (result, error, id) => [{ type: "JobNote", id }]
    }),
    createJobNote: builder.mutation<JobNote, { job_note: Partial<JobNote> }>({
      query: (body) => ({
        url: "/api/v2/job_notes",
        method: "POST",
        body
      }),
      invalidatesTags: ["JobNotes"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              message: "Job note created successfully",
              type: "success",
              placement: "bottomRight"
            })
          );
        } catch (_e) {
          dispatch(
            addNotification({
              message: "Failed to create job note",
              type: "danger",
              placement: "bottomRight"
            })
          );
        }
      }
    }),
    updateJobNote: builder.mutation<JobNote, { id: string; job_note: Partial<JobNote> }>({
      query: ({ id, job_note }) => ({
        url: `/api/v2/job_notes/${id}`,
        method: "PATCH",
        body: { job_note }
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "JobNote", id },
        "JobNotes"
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              message: "Job note updated successfully",
              type: "success",
              placement: "bottomRight"
            })
          );
        } catch (_e) {
          dispatch(
            addNotification({
              message: "Failed to update job note",
              type: "danger",
              placement: "bottomRight"
            })
          );
        }
      }
    }),
    deleteJobNote: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/v2/job_notes/${id}`,
        method: "DELETE"
      }),
      invalidatesTags: ["JobNotes"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            addNotification({
              message: "Job note deleted successfully",
              type: "success",
              placement: "bottomRight"
            })
          );
        } catch (_e) {
          dispatch(
            addNotification({
              message: "Failed to delete job note",
              type: "danger",
              placement: "bottomRight"
            })
          );
        }
      }
    })
  })
});

export const {
  useGetJobNotesQuery,
  useGetJobNoteQuery,
  useCreateJobNoteMutation,
  useUpdateJobNoteMutation,
  useDeleteJobNoteMutation
} = jobNotesApi;
